// Recursively compare two values, treating null / undefined / '' as equivalent.
// This handles fields the server omits entirely being set to '' by v-model inputs.
// JSON strings are parsed and compared structurally so that:
//   - whitespace differences in serialisation don't register as dirty
//   - '[]' / '{}' are treated as equivalent to '' (empty field)
function deepEqual(a, b) {
  const emptyish = v => v === null || v === undefined || v === ''
  const emptyJson = v => v === '[]' || v === '{}'
  if (emptyish(a) && emptyish(b)) return true
  if ((emptyish(a) || emptyJson(a)) && (emptyish(b) || emptyJson(b))) return true
  if (a === b) return true
  // If either side is a JSON string, parse both and compare structurally.
  if (typeof a === 'string' && typeof b === 'string') {
    const looksJson = s => { const t = s.trim(); return t[0] === '[' || t[0] === '{' }
    if (looksJson(a) || looksJson(b)) {
      try { return deepEqual(JSON.parse(a), JSON.parse(b)) } catch (e) { /* fall through */ }
    }
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((v, i) => deepEqual(v, b[i]))
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)])
    for (const key of keys) {
      if (!deepEqual(a[key], b[key])) return false
    }
    return true
  }
  return false
}

export default {
  data() {
    return {
      modelSnapshot: null,
      saving: false,
    }
  },
  computed: {
    isDirty() {
      if (this.modelSnapshot === null) return false
      const current = JSON.stringify(this.model)
      // Fast path: identical serialisation
      if (current === this.modelSnapshot) return false
      // Slow path: normalised comparison for null/undefined/'' equivalence
      return !deepEqual(this.model, JSON.parse(this.modelSnapshot))
    }
  },
  methods: {
    _resetDirtyTracking() {
      this.modelSnapshot = JSON.stringify(this.model)
      // Clear any stale validation errors and pending timers from the
      // numeric/filter mixins so a discard doesn't leave red fields behind.
      if ('numericErrors' in this) this.numericErrors = {}
      if ('filterErrors' in this) this.filterErrors = {}
      if (this._numericTimers) { Object.values(this._numericTimers).forEach(clearTimeout); this._numericTimers = {} }
      if (this._filterTimers) { Object.values(this._filterTimers).forEach(clearTimeout); this._filterTimers = {} }
      if (this._numericPendingArgs) this._numericPendingArgs = {}
      if (this._filterPendingArgs) this._filterPendingArgs = {}
    },
    _firstDirtyField() {
      if (this.modelSnapshot === null) return null
      const snapshot = JSON.parse(this.modelSnapshot)
      const allKeys = new Set([...Object.keys(this.model), ...Object.keys(snapshot)])
      for (const key of allKeys) {
        if (!deepEqual(this.model[key], snapshot[key])) return key
      }
      return null
    },
    scrollToFirstDirtyField() {
      const field = this._firstDirtyField()
      if (!field) return

      // Resolve which accordion section (toggle key) this field belongs to
      let toggleKey = null

      // 1. JSON editor fields explicitly mapped in each component
      if (this.jsonFieldToggleMap && this.jsonFieldToggleMap[field]) {
        toggleKey = this.jsonFieldToggleMap[field]
      }

      // 2. Regular fields mapped via prefix convention in each component
      if (!toggleKey && this.fieldPrefixSectionMap) {
        for (const [prefix, key] of this.fieldPrefixSectionMap) {
          if (field.startsWith(prefix)) {
            toggleKey = key
            break
          }
        }
      }

      // Track whether section was already open BEFORE we open it
      const sectionWasClosed = toggleKey && this.toggle && !this.toggle[toggleKey]

      // Open the section if we found one
      if (toggleKey && this.toggle !== undefined) {
        this.$set(this.toggle, toggleKey, true)
      }

      // If we just opened a collapsed section wait for its CSS transition (~350ms)
      // before querying layout — otherwise the row is still at height 0
      const delay = sectionWasClosed ? 420 : 0

      const doHighlight = () => {
        // Find the exact field row, fall back to JSON editor element, then section card, then component root
        const row = field ? this.$el.querySelector(`[data-field="${field}"]`) : null
        const jsonEl = field ? this.$el.querySelector(`[data-json-field="${field}"]`) : null

        let scrollTarget = this.$el
        if (row) {
          scrollTarget = row
        } else if (jsonEl) {
          scrollTarget = jsonEl
        } else if (toggleKey) {
          const cards = this.$el.querySelectorAll('.alert-section-card')
          for (const card of cards) {
            if (card.getAttribute('data-section') === toggleKey) {
              scrollTarget = card
              break
            }
          }
        }

        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // For JSON editor fields flash the first data row (tr containing inputs)
        // inside the editor, falling back to the whole editor div. For regular
        // fields flash the first input, falling back to the row cells.
        let flashEl
        if (scrollTarget.tagName === 'TR') {
          const jsonEditor = scrollTarget.querySelector('[data-json-field]')
          if (jsonEditor) {
            flashEl = jsonEditor
          } else {
            const input = scrollTarget.querySelector('input, select, textarea, .custom-select')
            flashEl = input || scrollTarget
          }
        } else {
          flashEl = scrollTarget
        }

        flashEl.classList.remove('tc-error-highlight')
        void flashEl.offsetWidth
        flashEl.classList.add('tc-error-highlight')
        flashEl.addEventListener('animationend', () => flashEl.classList.remove('tc-error-highlight'), { once: true })
      }

      this.$nextTick(() => setTimeout(doHighlight, delay))
    },
  }
}
